export interface Target {
    public_description: string
    short_description: string
    description: string
    name: string

    confidence_level: number
    danger_level: number
    risk_level: number

    last_update: number

    match_type: string
}

export interface RelatedRecord {
    record: string
    relationship: string
    address?: string
    lastSeen?: number
    targets?: string[]
}

export interface IocData {
    ioc: string
    record: string
    first_identified: number
    last_used: number
    blocker: Target
}

export interface ThreatStatus {
    active: boolean
    risk: number
    severity: number
    confidence: number
}

export interface IocInfo {
    country: string
    firstSeen?: number
    lastSeen?: number
    hits: number
    targets: string[]
}

export interface WhoisEntry {
    address: string
    cidr: string
    city: string
    country: string
    created: string
    description: string
    emails: string
    handle: string
    name: string
    organization: string
    postal_code: string
    range: string
    state: string
    updated: string
}

export interface WhoisIPData {
    asn: string
    asn_cidr: string
    asn_country_code: string
    asn_date: string
    asn_registry: string
    nets: WhoisEntry[]
    query: string
}

export interface WhoisDomainData {
    contacts: WhoisEntry[]
    creation_date: number
    expiration_date: number
    updated_date: number
    dnssec: string
    domain_name: string
    name_servers: string[]
    referral_url: string|null
    registrar: string
    status: string[]
    whois_server: string
}

export interface WhoisData {
    asn_country_code: string
    contacts: WhoisEntry[]
}

export interface DnsData {
    additional: string[]
    answer: string[]
    authority: string[]
    command: string[]
    question: string[]
    stats: string[]
}

export class Ioc {
    ioc: string
    info: IocInfo
    active: IocData[]
    history: IocData[]
    related: RelatedRecord[]
    threatStatus: ThreatStatus
    whois?: WhoisIPData|WhoisDomainData
    dns?: DnsData

    constructor(allData: any) {
        let iocData
        if (allData.hasOwnProperty('ioc') && allData.hasOwnProperty('whois')) {
            iocData = allData.ioc._data[0]
            this.whois = allData.whois._data[0]
            this.dns = allData.dig._data[0]
        } else {
            iocData = allData
        }

        this.ioc = iocData.ioc
        this.active = iocData.info.active.map((v: any) => {
            return {
                ioc: v.ioc,
                record: this._getRecord(v),
                first_identified: v.first_identified,
                last_used: v.last_used,
                blocker: v.blocker,
            }
        })
        this.history = iocData.info.history.map((v: any) => {
            return {
                ioc: v.ioc,
                record: this._getRecord(v),
                first_identified: v.first_identified,
                last_used: v.last_used,
                blocker: v.blocker,
            }
        })

        this.related = []
        if (iocData.info.hasOwnProperty('related_records')) {
            iocData.info.related_records.forEach((v: any) => {
                for (let k in v) if (v.hasOwnProperty(k)) {
                    this.related.push({
                        record: k,
                        relationship: v[k][0].relationship,
                        lastSeen: v[k][0]._data[0].last_used,
                        address: v[k][0]._data[0].address,
                        targets: [v[k][0]._data[0].blocker.short_description]
                    })
                }
            })
        }

        const risk = [...this.active, ...this.history]
            .filter(v => v.blocker.match_type !== 'udl')
            .map(v => v.blocker.risk_level)
            .reduce((acc, v) => Math.max(acc, v), -1)

        const activeSeverity = this.active
            .filter(v => v.blocker.match_type !== 'udl')
            .map(v => v.blocker.danger_level)
            .reduce((acc, v) => Math.max(acc, v), -1)
        const historySeverity = this.history
            .filter(v => v.blocker.match_type !== 'udl')
            .map(v => v.blocker.danger_level)
            .reduce((acc, v) => Math.max(acc, v), -1)

        const confidence = [...this.active, ...this.history]
            .filter(v => v.blocker.match_type !== 'udl')
            .map(v => v.blocker.confidence_level)
            .reduce((acc, v) => Math.max(acc, v), -1)

        this.threatStatus = {
            active: this.active.length > 0 && activeSeverity > 0,
            risk: risk,
            severity: Math.max(activeSeverity, historySeverity),
            confidence: confidence
        }

        const firstSeen = Math.min(
            this.active.map(v => v.first_identified).reduce((acc, v) => Math.min(acc, v), Infinity),
            this.history.map(v => v.first_identified).reduce((acc, v) => Math.min(acc, v), Infinity)
        )
        const lastSeen = Math.max(
            this.active.map(v => v.last_used).reduce((acc, v) => Math.max(acc, v), 0),
            this.history.map(v => v.last_used).reduce((acc, v) => Math.max(acc, v), 0)
        )
        let country = "Unknown"
        if (this.whois && 'asn_country_code' in this.whois) {
            country = this.whois.asn_country_code
        }

        this.info = {
            country: country,
            hits: iocData.log_hits,
            firstSeen: firstSeen !== Infinity ? firstSeen : undefined,
            lastSeen: lastSeen !== 0 ? lastSeen : undefined,
            targets: [
                ...this.active.map(v => v.blocker.short_description),
                ...this.history.map(v => v.blocker.short_description)
            ].filter((v, i, a) => a.indexOf(v) === i)
        }
    }

    _getRecord(info: any) {
        let record = info.ioc
        if (info.domain) {
            record = info.domain
        } else if (info.address_block) {
            record = info.address_block.start_address + ' - ' + info.address_block.end_address
        } else if (info.address) {
            record = info.address
        }
        return record
    }
}